import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import TemplateModal from './TemplateModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Templates {
  template_id: number;
  template_type: string;
  template_name: string;
  template_status: number;
  }

const Template: React.FC = () => {
  const [templates, setTemplates] = useState<Templates[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Templates | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchTemplate = async () => {
    try {
      const data = await apiRequest<Templates[]>({
        method: 'get',
        url: 'templates',
      });
      setTemplates(data);
    } catch (error) {
      showError('Failed to fetch templates');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchTemplate();
  }, []);

  const handleEditClick = (record: Templates) => {
    setIsEditMode(true);
    setInitialValues({
      template_id: record.template_id,
      template_type: record.template_type,
      template_name: record.template_name,
      template_status: record.template_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchTemplate();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (template_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-template/${template_id}`,
      });
      showSuccess('Template deleted');
      fetchTemplate();
    } catch {
      showError('Failed to delete Template');
    }
  };

  const handleStatusToggle = async (templateId: number) => {
    setTemplates((prev) =>
      prev.map((item) =>
        item.template_id === templateId
          ? { ...item, template_status: item.template_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-template/${templateId}`,
      });
      showSuccess('Template status updated');
    } catch (error) {
      showError('Failed to update status');
      setTemplates((prev) =>
        prev.map((item) =>
          item.template_id === templateId
            ? { ...item, template_status: item.template_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Templates>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Templates> = [
    {
      title: 'Template Name',
      dataIndex: 'template_name',
      key: 'template_name',
      filters: Array.from(new Set(templates.map((s) => s.template_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.template_name || null,
      onFilter: (value, record) => record.template_name.includes(value as string),
      sorter: (a, b) => a.template_name.localeCompare(b.template_name),
      sortOrder: sortedInfo.columnKey === 'template_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.template_name}</div>,
    },
    {
      title: 'Template Type',
      dataIndex: 'template_type',
      key: 'template_type',
      filters: Array.from(new Set(templates.map((s) => s.template_type))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.template_type || null,
      onFilter: (value, record) => record.template_type.includes(value as string),
      sorter: (a, b) => a.template_type.localeCompare(b.template_type),
      sortOrder: sortedInfo.columnKey === 'template_type' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.template_type}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: Templates) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.template_status}
                onChange={() => handleStatusToggle(record.template_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this template?"
              onConfirm={() => handleDelete(record.template_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Templates</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Template
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Templates>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="template_id"
          dataSource={templates.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No Template found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, templates.length)} of{' '}
                {templates.length}
              </span>
              <Pagination
                {...pagination}
                total={templates.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <TemplateModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Template;